import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { X, Send, Clock, Lightbulb, BookOpen, StickyNote, Plus, Trash2, Timer, Table, Grid3x3, Loader2, Zap, Users, Sparkles } from 'lucide-react';
import { trackFeatureUse } from '../utils/analytics';
import Tooltip from './Tooltip';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ThinkLab = ({ 
  sessionId, 
  sessionTitle, 
  isOpen, 
  onClose,
  timerActive,
  setTimerActive,
  timeRemaining,
  setTimeRemaining,
  trackingEnabled,
  setTrackingEnabled,
  activeReading,
  setActiveReading,
  trackedReadings,
  setTrackedReadings,
  activeTab,
  setActiveTab
}) => {
  const [brainstormMessage, setBrainstormMessage] = useState('');
  const [brainstormHistory, setBrainstormHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resources, setResources] = useState([]);
  const [newResourcesCount, setNewResourcesCount] = useState(0);
  const [showResourceDialog, setShowResourceDialog] = useState(false);
  const [pendingResource, setPendingResource] = useState(null);
  
  // Idea Board state
  const [ideas, setIdeas] = useState([]);
  const [newIdeaContent, setNewIdeaContent] = useState('');
  const [newIdeaColor, setNewIdeaColor] = useState('yellow');
  const [newIdeaSource, setNewIdeaSource] = useState('own'); // 'own', 'inspired', 'leading'
  const [ideaViewMode, setIdeaViewMode] = useState('sticky'); // 'sticky' or 'table'
  
  // Think Project state
  const [sprints, setSprints] = useState([]);
  const [currentSprint, setCurrentSprint] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('medium'); // 'low', 'medium', 'high'
  const [projectView, setProjectView] = useState('board'); // 'board', 'list', 'sprints', 'design-sprint'
  
  // Design Sprint state
  const [designSprints, setDesignSprints] = useState([]);
  const [activeDesignSprint, setActiveDesignSprint] = useState(null);
  const [currentSprintDay, setCurrentSprintDay] = useState(1); // 1-5 for 5-day sprint
  
  // Notice Board state
  const [pinnedResources, setPinnedResources] = useState([]);
  const [newPinUrl, setNewPinUrl] = useState('');
  const [newPinTitle, setNewPinTitle] = useState('');
  const [newPinNotes, setNewPinNotes] = useState('');
  
  // Journal state
  const [journalEntries, setJournalEntries] = useState([]);
  const [newJournalEntry, setNewJournalEntry] = useState('');
  const [journalSource, setJournalSource] = useState('general'); // 'professor', 'customer', 'stakeholder', 'general'
  
  // Stakeholder Analysis state
  const [stakeholderData, setStakeholderData] = useState(null);
  const [loadingStakeholders, setLoadingStakeholders] = useState(false);
  
  // Reading tracker state (now passed as props - trackedReadings, setTrackedReadings)
  
  // Timer alarm state
  const [showAlarm, setShowAlarm] = useState(false);
  const [alarmType, setAlarmType] = useState('initial'); // 'initial', 'snooze'
  const [showSparkInput, setShowSparkInput] = useState(false);
  const [sparkText, setSparkText] = useState('');
  
  // Timer state (now passed as props - timerActive, timeRemaining)
  const [timerMinutes, setTimerMinutes] = useState(45);

  // Load ideas when opened
  useEffect(() => {
    if (isOpen && sessionId) {
      loadIdeas();
      // Track ThinkLab usage
      trackFeatureUse('think_lab', { session_id: sessionId });
    }
  }, [isOpen, sessionId]);

  // Timer countdown
  useEffect(() => {
    let interval;
    if (timerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setTimerActive(false);
            setShowAlarm(true); // Show smart alarm instead of just toast
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerActive, timeRemaining]);

  const loadIdeas = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/thinklab/ideas/${sessionId}`);
      setIdeas(response.data);
    } catch (error) {
      console.error('Failed to load ideas:', error);
    }
  };

  const handleBrainstorm = async () => {
    if (!brainstormMessage.trim()) return;
    
    const userMsg = brainstormMessage;
    setBrainstormMessage('');
    setBrainstormHistory([
      ...brainstormHistory,
      { type: 'user', content: userMsg }
    ]);
    
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/thinklab/brainstorm`, {
        session_id: sessionId,
        message: userMsg
      });
      
      setBrainstormHistory(prev => [
        ...prev,
        { type: 'ai', content: response.data.response }
      ]);

      // Check if resources were found
      if (response.data.resources && response.data.resources.length > 0) {
        const newResources = response.data.resources;
        // Show first resource in quick dialog
        setPendingResource(newResources[0]);
        setShowResourceDialog(true);
        
        // Store all resources for later
        if (newResources.length > 1) {
          setNewResourcesCount(prev => prev + newResources.length - 1);
        }
        
        // Auto-dismiss dialog after 8 seconds
        setTimeout(() => {
          if (showResourceDialog) {
            handleDismissResource();
          }
        }, 8000);
      }
    } catch (error) {
      console.error('Brainstorm error:', error);
      toast({
        title: "Brainstorm Failed",
        description: error.response?.data?.detail || "Could not connect to AI. Please try again.",
        variant: "destructive"
      });
      setBrainstormHistory(prev => [
        ...prev,
        { type: 'ai', content: "Sorry, I'm having trouble connecting. Please try again." }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleResourceClick = (resource) => {
    // Start 30-minute timer automatically
    setTimerMinutes(30);
    setTimeRemaining(30 * 60);
    setTimerActive(true);
    setAlarmType('initial');
    
    // Track reading if enabled
    if (trackingEnabled) {
      const reading = {
        id: Date.now(),
        resource,
        startTime: new Date(),
        timeboxMinutes: 30,
        status: 'reading'
      };
      setActiveReading(reading);
    }
    
    toast({
      title: "⏱️ Timer Started",
      description: "30-minute reading timer activated. You'll be prompted when time's up!",
    });
    
    // Open link in new tab
    window.open(resource.url, '_blank');
  };

  const handleAlarmDismiss = () => {
    setShowAlarm(false);
    setTimerActive(false);
    if (activeReading) {
      const updatedReading = {
        ...activeReading,
        status: 'dismissed',
        endTime: new Date()
      };
      setTrackedReadings([...trackedReadings, updatedReading]);
      setActiveReading(null);
    }
  };

  const handleAlarmSnooze = () => {
    setTimeRemaining(10 * 60);
    setAlarmType('snooze');
    setShowAlarm(false);
    toast({
      title: "⏰ Snoozed",
      description: "10 more minutes added"
    });
  };

  const handleQuickSpark = () => {
    if (sparkText.trim() && sparkText.split(' ').length <= 5) {
      handleAddIdea(sparkText, 'inspired');
      setSparkText('');
      setShowSparkInput(false);
      setShowAlarm(false);
      
      if (activeReading) {
        const updatedReading = {
          ...activeReading,
          status: 'spark-captured',
          endTime: new Date(),
          spark: sparkText
        };
        setTrackedReadings([...trackedReadings, updatedReading]);
        setActiveReading(null);
      }
    }
  };

  const handleKeepResource = () => {
    if (pendingResource) {
      setResources([...resources, pendingResource]);
      setNewResourcesCount(prev => prev + 1);
      toast({
        title: "📚 Resource Saved",
        description: "Check Resources tab"
      });
    }
    setShowResourceDialog(false);
    setPendingResource(null);
  };

  const handleDismissResource = () => {
    setShowResourceDialog(false);
    setPendingResource(null);
  };

  const handleAddIdea = async (content = null, source = null) => {
    const ideaContent = content || newIdeaContent;
    const ideaSource = source || newIdeaSource;
    
    if (!ideaContent.trim()) return;
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/thinklab/ideas`, {
        session_id: sessionId,
        content: ideaContent,
        color: newIdeaColor,
        tags: [ideaSource], // Store source as tag
        storage_type: 'later'
      });
      
      if (response.data.success && response.data.idea) {
        setIdeas([...ideas, response.data.idea]);
        setNewIdeaContent('');
        setNewIdeaColor('yellow');
        setNewIdeaSource('own');
        toast({
          title: "💡 Idea Captured!",
          description: `Logged as "${ideaSource}" thought`
        });
      }
    } catch (error) {
      console.error('Failed to save idea:', error);
      toast({
        title: "Failed to Save",
        description: error.response?.data?.detail || error.message || "Could not save idea. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteIdea = async (ideaId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/thinklab/ideas/${ideaId}`);
      setIdeas(ideas.filter(i => i.id !== ideaId));
      toast({
        title: "Idea Deleted",
        description: "Sticky note removed"
      });
    } catch (error) {
      toast({
        title: "Failed to Delete",
        variant: "destructive"
      });
    }
  };

  const startTimer = (minutes) => {
    setTimerMinutes(minutes);
    setTimeRemaining(minutes * 60);
    setTimerActive(true);
    toast({
      title: "⏱️ Timer Started",
      description: `${minutes} minute timer is running. You'll be prompted to capture insights.`
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  // Detect if running in popup window
  const isPopup = window.name === 'ThinkLabPopup' || window.location.pathname === '/think-lab-popup';

  return (
    <div className={isPopup ? "" : "fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"}>
      <Card className={isPopup ? "w-full shadow-none" : "w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col bg-white shadow-2xl rounded-2xl border-0"}>
        {/* Enhanced Header with Gradient */}
        <CardHeader className="border-b-0 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white relative overflow-hidden">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
            <div className="absolute bottom-0 right-0 w-48 h-48 bg-white rounded-full translate-x-24 translate-y-24"></div>
          </div>
          
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-3 text-2xl font-bold">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-yellow-300" />
                </div>
                Think Lab
                <Badge className="bg-white/20 text-white border-white/30 text-xs">
                  ✨ AI-Powered
                </Badge>
              </CardTitle>
              <p className="text-sm text-white/80 mt-2">AI scales your thought execution - you lead the thinking</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const width = 450;
                  const height = 700;
                  const left = window.screen.width - width - 20;
                  const top = 20;
                  
                  const popup = window.open(
                    `/think-lab-popup?session=${sessionId}&title=${encodeURIComponent(sessionTitle)}`,
                    'ThinkLabPopup',
                    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
                  );
                  
                  if (popup) {
                    toast({
                      title: "🪟 Think Lab Popped Out!",
                      description: "Keep this window open while browsing external sites.",
                      duration: 8000
                    });
                  } else {
                    toast({
                      title: "Popup Blocked",
                      description: "Please allow popups for this site to use this feature",
                      variant: "destructive"
                    });
                  }
                }}
                className="text-xs bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                <span className="mr-1">🪟</span>
                Pop Out
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onClose}
                className="text-white hover:bg-white/20"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
          <div className="relative z-10 mt-3 flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <p className="text-sm text-white/90 font-medium">Session: {sessionTitle}</p>
          </div>
        </CardHeader>

        {/* Enhanced Tabs with Pills Design */}
        <div className="flex gap-2 p-3 bg-gradient-to-r from-slate-50 to-purple-50 border-b overflow-x-auto">
          <Tooltip content="Chat with AI for ideation" position="bottom">
            <Button
              variant={activeTab === 'brainstorm' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('brainstorm')}
              className={`rounded-full px-4 ${activeTab === 'brainstorm' ? 'bg-gradient-to-r from-purple-600 to-pink-600 shadow-lg' : 'hover:bg-purple-100'}`}
            >
              <Send className="w-4 h-4 mr-2" />
              Brainstorm
            </Button>
          </Tooltip>
          <Tooltip content="Auto-curated resources" position="bottom">
            <Button
              variant={activeTab === 'resources' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => {
                setActiveTab('resources');
                setNewResourcesCount(0);
              }}
              className={`rounded-full px-4 relative ${activeTab === 'resources' ? 'bg-gradient-to-r from-green-600 to-emerald-600 shadow-lg' : 'hover:bg-green-100'}`}
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Resources
              {newResourcesCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                  {newResourcesCount}
                </span>
              )}
            </Button>
          </Tooltip>
          <Tooltip content="Smart thinking timer" position="bottom">
            <Button
              variant={activeTab === 'timer' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('timer')}
              className={`rounded-full px-4 ${activeTab === 'timer' ? 'bg-gradient-to-r from-orange-500 to-red-500 shadow-lg' : 'hover:bg-orange-100'}`}
            >
              <Clock className="w-4 h-4 mr-2" />
              Timer
              {timerActive && (
                <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">
                  {formatTime(timeRemaining)}
                </span>
              )}
            </Button>
          </Tooltip>
          <Tooltip content="Capture ideas visually" position="bottom">
            <Button
              variant={activeTab === 'ideaboard' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('ideaboard')}
              className={`rounded-full px-4 ${activeTab === 'ideaboard' ? 'bg-gradient-to-r from-yellow-500 to-orange-500 shadow-lg' : 'hover:bg-yellow-100'}`}
            >
              <StickyNote className="w-4 h-4 mr-2" />
              Ideas
            </Button>
          </Tooltip>
          <Tooltip content="Task tracking & sprints" position="bottom">
            <Button
              variant={activeTab === 'project' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('project')}
              className={`rounded-full px-4 ${activeTab === 'project' ? 'bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg' : 'hover:bg-indigo-100'}`}
            >
              <Table className="w-4 h-4 mr-2" />
              Project
            </Button>
          </Tooltip>
          <Tooltip content="Pin important links" position="bottom">
            <Button
              variant={activeTab === 'noticeboard' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('noticeboard')}
              className={`rounded-full px-4 ${activeTab === 'noticeboard' ? 'bg-gradient-to-r from-blue-600 to-cyan-600 shadow-lg' : 'hover:bg-blue-100'}`}
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Board
            </Button>
          </Tooltip>
          <Tooltip content="Document your journey" position="bottom">
            <Button
              variant={activeTab === 'journal' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('journal')}
              className={`rounded-full px-4 ${activeTab === 'journal' ? 'bg-gradient-to-r from-teal-600 to-green-600 shadow-lg' : 'hover:bg-teal-100'}`}
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Journal
            </Button>
          </Tooltip>
          <Tooltip content="Map your stakeholders" position="bottom">
            <Button
              variant={activeTab === 'stakeholders' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('stakeholders')}
              className={`rounded-full px-4 ${activeTab === 'stakeholders' ? 'bg-gradient-to-r from-pink-600 to-rose-600 shadow-lg' : 'hover:bg-pink-100'}`}
            >
              <Users className="w-4 h-4 mr-2" />
              Who For?
            </Button>
          </Tooltip>
        </div>

        <CardContent className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-slate-50 via-purple-50/30 to-pink-50/30"
                     style={{
                       backgroundImage: `
                         linear-gradient(rgba(168, 85, 247, 0.05) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(168, 85, 247, 0.05) 1px, transparent 1px)
                       `,
                       backgroundSize: '24px 24px'
                     }}>
          {/* Brainstorm Tab - Conversational Thought Partner */}
          {activeTab === 'brainstorm' && (
            <div className="flex flex-col h-[500px]">
              {/* Welcome message if no history */}
              {brainstormHistory.length === 0 && (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center max-w-md animate-in fade-in duration-500">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-yellow-400 via-orange-400 to-pink-500 flex items-center justify-center shadow-lg animate-pulse">
                      <Sparkles className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Hey there! I'm Spark ✨</h3>
                    <p className="text-slate-600 mb-4">
                      Your creative thought partner. Let's explore your ideas together!
                    </p>
                    <div className="space-y-2">
                      <p className="text-sm text-slate-500">Try asking me:</p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {[
                          "What makes my idea unique?",
                          "How can I validate this?",
                          "What's missing?",
                          "Who would use this?"
                        ].map((prompt, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              setBrainstormMessage(prompt);
                            }}
                            className="px-3 py-1.5 text-xs bg-white border border-purple-200 rounded-full hover:bg-purple-50 hover:border-purple-400 transition-all text-purple-700"
                          >
                            {prompt}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Chat History */}
              {brainstormHistory.length > 0 && (
                <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2">
                  {brainstormHistory.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex items-start gap-3 animate-in slide-in-from-bottom duration-300 ${
                        msg.type === 'user' ? 'flex-row-reverse' : ''
                      }`}
                      style={{ animationDelay: `${idx * 50}ms` }}
                    >
                      {/* Avatar */}
                      {msg.type === 'ai' && (
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 via-orange-400 to-pink-500 flex items-center justify-center flex-shrink-0 shadow-md">
                          <Sparkles className="w-5 h-5 text-white" />
                        </div>
                      )}
                      {msg.type === 'user' && (
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-md">
                          <span className="text-white text-sm font-bold">Y</span>
                        </div>
                      )}
                      
                      {/* Message Bubble */}
                      <div
                        className={`max-w-[80%] p-3 rounded-2xl shadow-sm ${
                          msg.type === 'user'
                            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-br-md'
                            : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  
                  {/* Typing indicator */}
                  {loading && (
                    <div className="flex items-start gap-3 animate-in fade-in duration-200">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 via-orange-400 to-pink-500 flex items-center justify-center flex-shrink-0 shadow-md">
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                      <div className="bg-white border border-slate-200 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                        <div className="flex gap-1.5">
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Input Area */}
              <div className="border-t border-slate-200 pt-4 mt-auto bg-gradient-to-t from-white via-white to-transparent">
                <div className="flex gap-2 items-end">
                  <div className="flex-1 relative">
                    <Textarea
                      value={brainstormMessage}
                      onChange={(e) => setBrainstormMessage(e.target.value)}
                      placeholder="Share your thoughts... I'm all ears! 👂"
                      className="resize-none min-h-[60px] max-h-[120px] bg-white border-2 border-purple-200 rounded-xl focus:border-purple-400 focus:ring-2 focus:ring-purple-100 pr-4 shadow-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleBrainstorm();
                        }
                      }}
                    />
                  </div>
                  <Button
                    onClick={handleBrainstorm}
                    disabled={loading || !brainstormMessage.trim()}
                    className="h-[60px] w-[60px] rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-2 text-center">
                  Press Enter to send • Shift+Enter for new line
                </p>
              </div>
            </div>
          )}

          {/* Resources Tab */}
          {activeTab === 'resources' && (
            <div className="space-y-4">
              {/* Prominent Track Toggle */}
              <Card className={`border-2 ${trackingEnabled ? 'border-green-400 bg-green-50' : 'border-slate-300 bg-white'} shadow-lg`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        trackingEnabled ? 'bg-green-500' : 'bg-slate-200'
                      }`}>
                        <BookOpen className={`w-6 h-6 ${trackingEnabled ? 'text-white' : 'text-slate-500'}`} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Track My Reading</h4>
                        <p className="text-xs text-slate-600">
                          {trackingEnabled 
                            ? '✓ Tracking time, learnings & relevance' 
                            : 'Enable to monitor your research journey'}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={trackingEnabled}
                        onChange={(e) => {
                          setTrackingEnabled(e.target.checked);
                          toast({
                            title: e.target.checked ? "📊 Tracking Enabled" : "Tracking Disabled",
                            description: e.target.checked 
                              ? "Your reading sessions will be monitored" 
                              : "Reading tracking turned off"
                          });
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-14 h-7 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500"></div>
                    </label>
                  </div>
                </CardContent>
              </Card>

              <div className="p-4 bg-green-50 rounded-lg border border-green-200 shadow-sm">
                <h3 className="font-semibold text-green-900 mb-2">📚 Learning Resources</h3>
                <p className="text-sm text-green-800">AI-curated content appears automatically as you brainstorm</p>
              </div>
              
              {resources.length > 0 ? (
                <div className="space-y-3">
                  {resources.map((resource, idx) => (
                    <Card key={idx} className="border-2 border-green-200 hover:border-green-400 transition-all bg-white shadow-md">
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            resource.type === 'paper' ? 'bg-blue-100' : 'bg-red-100'
                          }`}>
                            {resource.type === 'paper' ? '📄' : '🎥'}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-900 mb-1">{resource.title}</h4>
                            <p className="text-sm text-slate-600 mb-2">{resource.description}</p>
                            <button
                              onClick={() => handleResourceClick(resource)}
                              className="text-sm text-blue-600 hover:text-blue-800 underline font-medium"
                            >
                              {resource.type === 'paper' ? 'Read Paper' : 'Watch Video'} → 
                              {trackingEnabled && ' (Auto-timer: 30 min)'}
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                  <p className="text-lg font-medium mb-2">No resources yet</p>
                  <p className="text-sm">Start brainstorming! Relevant resources will appear automatically.</p>
                </div>
              )}

              {/* Tracked Readings Summary */}
              {trackingEnabled && trackedReadings.length > 0 && (
                <Card className="border-2 border-purple-200 bg-purple-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-purple-900">📊 Reading Summary</h4>
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            setLoading(true);
                            const response = await axios.post(`${BACKEND_URL}/api/thinklab/generate-tasks-from-reading`, {
                              session_id: sessionId,
                              tracked_readings: trackedReadings.map(r => ({
                                title: r.resource.title,
                                time_spent: Math.round((new Date(r.endTime || new Date()) - new Date(r.startTime)) / 60000)
                              }))
                            });
                            
                            if (response.data.success && response.data.tasks.length > 0) {
                              // Add generated tasks to task list
                              const newTasks = response.data.tasks.map(task => ({
                                id: Date.now().toString() + Math.random(),
                                title: task.title,
                                description: task.description,
                                priority: task.priority,
                                status: 'todo',
                                category: task.category,
                                created_at: new Date().toISOString(),
                                source: 'ai_generated'
                              }));
                              
                              setTasks([...tasks, ...newTasks]);
                              setActiveTab('project');
                              
                              toast({
                                title: "🤖 AI Tasks Generated!",
                                description: `${newTasks.length} tasks created from your reading activity. Check Think Project tab.`,
                                duration: 5000
                              });
                            } else {
                              toast({
                                title: "No Tasks Generated",
                                description: "Try reading more resources first",
                                variant: "destructive"
                              });
                            }
                            setLoading(false);
                          } catch (error) {
                            console.error('Task generation failed:', error);
                            toast({
                              title: "Generation Failed",
                              description: "Could not generate tasks. Please try again.",
                              variant: "destructive"
                            });
                            setLoading(false);
                          }
                        }}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Zap className="w-4 h-4 mr-2" />
                            Generate Tasks
                          </>
                        )}
                      </Button>
                    </div>
                    <div className="space-y-2 mb-4">
                      {trackedReadings.map((reading, idx) => {
                        const exceeded = reading.endTime && 
                          (new Date(reading.endTime) - new Date(reading.startTime)) / 60000 > reading.timeboxMinutes;
                        return (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <span className={exceeded ? 'text-2xl' : 'text-2xl'}>
                              {exceeded ? '🩷' : '🟢'}
                            </span>
                            <span className="flex-1 text-slate-700">{reading.resource.title}</span>
                            <span className="text-xs text-slate-500">
                              {reading.status}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-purple-700 text-center">
                      💡 AI can convert your reading into actionable tasks
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Timer Tab */}
          {activeTab === 'timer' && (
            <div className="space-y-6">
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h3 className="font-semibold text-orange-900 mb-2">Time-boxed Thinking</h3>
                <p className="text-sm text-orange-800">Set focused work intervals. You'll be prompted to capture learnings when time's up.</p>
              </div>

              {timerActive ? (
                <div className="text-center py-12">
                  <Timer className="w-20 h-20 mx-auto mb-6 text-orange-600 animate-pulse" />
                  <div className="text-6xl font-bold text-slate-900 mb-4">
                    {formatTime(timeRemaining)}
                  </div>
                  <p className="text-slate-600 mb-6">Time remaining</p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setTimerActive(false);
                      setTimeRemaining(null);
                    }}
                  >
                    Stop Timer
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  <button
                    onClick={() => startTimer(30)}
                    className="p-6 rounded-lg border-2 border-orange-300 bg-white hover:border-orange-500 hover:shadow-lg transition-all text-left"
                  >
                    <div className="text-4xl font-bold text-orange-600 mb-2">30 min</div>
                    <p className="text-sm text-slate-600">Quick exploration</p>
                  </button>
                  <button
                    onClick={() => startTimer(45)}
                    className="p-6 rounded-lg border-2 border-orange-400 bg-white hover:border-orange-600 hover:shadow-lg transition-all text-left"
                  >
                    <div className="text-4xl font-bold text-orange-600 mb-2">45 min</div>
                    <p className="text-sm text-slate-600">Deep work session</p>
                  </button>
                  <button
                    onClick={() => startTimer(60)}
                    className="p-6 rounded-lg border-2 border-orange-500 bg-white hover:border-orange-700 hover:shadow-lg transition-all text-left"
                  >
                    <div className="text-4xl font-bold text-orange-600 mb-2">60 min</div>
                    <p className="text-sm text-slate-600">Extended focus</p>
                  </button>
                  <button
                    onClick={() => {
                      const custom = prompt('Enter minutes (1-120):');
                      if (custom && !isNaN(custom) && custom > 0 && custom <= 120) {
                        startTimer(parseInt(custom));
                      }
                    }}
                    className="p-6 rounded-lg border-2 border-slate-300 bg-white hover:border-orange-500 hover:shadow-lg transition-all text-left"
                  >
                    <div className="text-4xl font-bold text-slate-600 mb-2">Custom</div>
                    <p className="text-sm text-slate-600">Set your own time</p>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Idea Board Tab */}
          {activeTab === 'ideaboard' && (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-yellow-900 mb-2">💡 Idea Board</h3>
                    <p className="text-sm text-yellow-800">Capture insights, learnings, and new ideas</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={ideaViewMode === 'sticky' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setIdeaViewMode('sticky')}
                    >
                      <Grid3x3 className="w-4 h-4 mr-2" />
                      Sticky Notes
                    </Button>
                    <Button
                      variant={ideaViewMode === 'table' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setIdeaViewMode('table')}
                    >
                      <Table className="w-4 h-4 mr-2" />
                      Table View
                    </Button>
                  </div>
                </div>
              </div>

              {/* Add New Idea */}
              <Card className="border-2 border-yellow-400 bg-white shadow-lg">
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <Textarea
                      value={newIdeaContent}
                      onChange={(e) => setNewIdeaContent(e.target.value)}
                      placeholder="Write your insight, learning, or new idea here..."
                      className="min-h-[100px] bg-white border-2 border-yellow-300 shadow-sm"
                    />
                    
                    {/* Thought Source Categorization */}
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-sm font-semibold text-purple-900 mb-2">What kind of thought is this?</p>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          onClick={() => setNewIdeaSource('own')}
                          className={`p-2 rounded-lg text-xs transition-all ${
                            newIdeaSource === 'own'
                              ? 'bg-purple-600 text-white shadow-md'
                              : 'bg-white border border-purple-300 text-purple-800 hover:border-purple-500'
                          }`}
                        >
                          💭 Own Thought
                        </button>
                        <button
                          onClick={() => setNewIdeaSource('inspired')}
                          className={`p-2 rounded-lg text-xs transition-all ${
                            newIdeaSource === 'inspired'
                              ? 'bg-purple-600 text-white shadow-md'
                              : 'bg-white border border-purple-300 text-purple-800 hover:border-purple-500'
                          }`}
                        >
                          ✨ Inspired
                        </button>
                        <button
                          onClick={() => setNewIdeaSource('leading')}
                          className={`p-2 rounded-lg text-xs transition-all ${
                            newIdeaSource === 'leading'
                              ? 'bg-purple-600 text-white shadow-md'
                              : 'bg-white border border-purple-300 text-purple-800 hover:border-purple-500'
                          }`}
                        >
                          🔗 Leading Thought
                        </button>
                      </div>
                      <p className="text-xs text-purple-700 mt-2">
                        {newIdeaSource === 'own' && '💭 Original idea from your own thinking'}
                        {newIdeaSource === 'inspired' && '✨ Inspired by what you read/watched'}
                        {newIdeaSource === 'leading' && '🔗 From resources but could become something new'}
                      </p>
                    </div>

                    <div className="flex gap-2 items-center">
                      <div className="flex gap-2">
                        {['yellow', 'pink', 'blue', 'green', 'purple'].map(color => (
                          <button
                            key={color}
                            onClick={() => setNewIdeaColor(color)}
                            className={`w-8 h-8 rounded-full border-3 shadow-md ${
                              newIdeaColor === color ? 'border-slate-900 ring-2 ring-slate-400' : 'border-slate-300'
                            }`}
                            style={{ backgroundColor: 
                              color === 'yellow' ? '#fef08a' :
                              color === 'pink' ? '#fbcfe8' :
                              color === 'blue' ? '#bfdbfe' :
                              color === 'green' ? '#bbf7d0' :
                              '#e9d5ff'
                            }}
                          />
                        ))}
                      </div>
                      <Button
                        onClick={() => handleAddIdea()}
                        disabled={!newIdeaContent.trim()}
                        className="ml-auto bg-yellow-600 shadow-lg"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Idea
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Table View */}
              {ideaViewMode === 'table' && (
                <div className="bg-white rounded-lg border-2 border-slate-300 shadow-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-100 border-b-2 border-slate-300">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 w-16">Color</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Idea / Insight</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 w-32">Date</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ideas.map((idea, idx) => (
                        <tr key={idea.id} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-blue-50`}>
                          <td className="px-4 py-3">
                            <div
                              className="w-8 h-8 rounded border-2 border-slate-300"
                              style={{
                                backgroundColor:
                                  idea.color === 'yellow' ? '#fef08a' :
                                  idea.color === 'pink' ? '#fbcfe8' :
                                  idea.color === 'blue' ? '#bfdbfe' :
                                  idea.color === 'green' ? '#bbf7d0' :
                                  '#e9d5ff'
                              }}
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800">{idea.content}</td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {new Date(idea.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleDeleteIdea(idea.id)}
                              className="text-slate-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {ideas.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <Table className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                      <p className="text-lg font-medium">No ideas yet</p>
                      <p className="text-sm">Add your first insight above</p>
                    </div>
                  )}
                </div>
              )}

              {/* Sticky Notes Grid */}
              {ideaViewMode === 'sticky' && (
                <>
                  <div className="grid md:grid-cols-3 gap-4">
                    {ideas.map(idea => (
                      <Card
                        key={idea.id}
                        className="border-2 shadow-lg hover:shadow-xl transition-shadow transform hover:-translate-y-1"
                        style={{
                          backgroundColor:
                            idea.color === 'yellow' ? '#fef08a' :
                            idea.color === 'pink' ? '#fbcfe8' :
                            idea.color === 'blue' ? '#bfdbfe' :
                            idea.color === 'green' ? '#bbf7d0' :
                            '#e9d5ff',
                          borderColor:
                            idea.color === 'yellow' ? '#fbbf24' :
                            idea.color === 'pink' ? '#f472b6' :
                            idea.color === 'blue' ? '#60a5fa' :
                            idea.color === 'green' ? '#4ade80' :
                            '#c084fc'
                        }}
                      >
                        <CardContent className="pt-6 relative">
                          <button
                            onClick={() => handleDeleteIdea(idea.id)}
                            className="absolute top-2 right-2 text-slate-600 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <p className="text-sm text-slate-800 whitespace-pre-wrap font-medium">{idea.content}</p>
                          <p className="text-xs text-slate-500 mt-3">
                            {new Date(idea.created_at).toLocaleDateString()}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {ideas.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <StickyNote className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                      <p className="text-lg font-medium">No sticky notes yet</p>
                      <p className="text-sm">Capture your insights above</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          
          {/* Think Project Tab */}
          {activeTab === 'project' && (
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                <h3 className="font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                  <Table className="w-5 h-5" />
                  Think Project: Turn Ideas into Action
                </h3>
                <p className="text-sm text-indigo-800">Sprint planning, task tracking, and project management for your ideas</p>
              </div>

              {/* View Switcher */}
              <div className="flex gap-2">
                <Button
                  variant={projectView === 'board' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setProjectView('board')}
                  className="flex-1"
                >
                  <Grid3x3 className="w-4 h-4 mr-2" />
                  Board
                </Button>
                <Button
                  variant={projectView === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setProjectView('list')}
                  className="flex-1"
                >
                  <Table className="w-4 h-4 mr-2" />
                  List
                </Button>
                <Button
                  variant={projectView === 'sprints' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setProjectView('sprints')}
                  className="flex-1"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Sprints
                </Button>
                <Button
                  variant={projectView === 'design-sprint' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setProjectView('design-sprint')}
                  className="flex-1"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Design Sprint
                </Button>
              </div>

              {/* Add Task Form */}
              <Card className="border-2 border-dashed border-indigo-300 bg-indigo-50/50">
                <CardContent className="pt-4">
                  <h4 className="font-semibold text-sm text-indigo-900 mb-3">Add New Task</h4>
                  <div className="space-y-3">
                    <Input
                      placeholder="Task title (e.g., Research competitor solutions)"
                      value={newTaskTitle}
                      onChange={(e) => setNewTaskTitle(e.target.value)}
                      className="bg-white"
                    />
                    <Textarea
                      placeholder="Task description (optional)"
                      value={newTaskDescription}
                      onChange={(e) => setNewTaskDescription(e.target.value)}
                      rows={2}
                      className="bg-white resize-none"
                    />
                    <div className="flex gap-2">
                      <select
                        value={newTaskPriority}
                        onChange={(e) => setNewTaskPriority(e.target.value)}
                        className="flex-1 p-2 border rounded-md text-sm"
                      >
                        <option value="low">🟢 Low Priority</option>
                        <option value="medium">🟡 Medium Priority</option>
                        <option value="high">🔴 High Priority</option>
                      </select>
                      <Button
                        onClick={() => {
                          if (!newTaskTitle.trim()) return;
                          
                          const newTask = {
                            id: Date.now().toString(),
                            title: newTaskTitle,
                            description: newTaskDescription,
                            priority: newTaskPriority,
                            status: 'todo',
                            created_at: new Date().toISOString()
                          };
                          
                          setTasks([...tasks, newTask]);
                          setNewTaskTitle('');
                          setNewTaskDescription('');
                          setNewTaskPriority('medium');
                          
                          toast({
                            title: "✅ Task Added",
                            description: `"${newTask.title}" added to ${currentSprint ? 'current sprint' : 'backlog'}`
                          });
                        }}
                        className="bg-indigo-600"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Board View */}
              {projectView === 'board' && (
                <div className="grid grid-cols-3 gap-4">
                  {/* To Do Column */}
                  <Card className="border-2 border-slate-200">
                    <CardHeader className="pb-3 bg-slate-50">
                      <CardTitle className="text-sm font-semibold text-slate-700">📋 To Do</CardTitle>
                      <p className="text-xs text-slate-500">{tasks.filter(t => t.status === 'todo').length} tasks</p>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-2 min-h-[200px]">
                      {tasks.filter(t => t.status === 'todo').map(task => (
                        <Card key={task.id} className={`border transition-shadow ${
                          task.source === 'ai_generated' ? 'border-purple-300 bg-purple-50/30' : 'border-slate-200'
                        } hover:shadow-md`}>
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h5 className="font-semibold text-sm text-slate-900">{task.title}</h5>
                                  {task.source === 'ai_generated' && (
                                    <Badge className="text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                                      🤖 AI
                                    </Badge>
                                  )}
                                </div>
                                {task.category && (
                                  <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                                    {task.category}
                                  </span>
                                )}
                              </div>
                              <Badge className={`text-xs ml-2 ${
                                task.priority === 'high' ? 'bg-red-500' :
                                task.priority === 'medium' ? 'bg-yellow-500' :
                                'bg-green-500'
                              }`}>
                                {task.priority}
                              </Badge>
                            </div>
                            {task.description && (
                              <p className="text-xs text-slate-600 mb-3">{task.description}</p>
                            )}
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 text-xs"
                                onClick={() => {
                                  setTasks(tasks.map(t => 
                                    t.id === task.id ? {...t, status: 'inprogress'} : t
                                  ));
                                }}
                              >
                                Start
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setTasks(tasks.filter(t => t.id !== task.id))}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      {tasks.filter(t => t.status === 'todo').length === 0 && (
                        <p className="text-center text-slate-400 text-sm py-8">No tasks</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* In Progress Column */}
                  <Card className="border-2 border-blue-200">
                    <CardHeader className="pb-3 bg-blue-50">
                      <CardTitle className="text-sm font-semibold text-blue-700">🔄 In Progress</CardTitle>
                      <p className="text-xs text-blue-500">{tasks.filter(t => t.status === 'inprogress').length} tasks</p>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-2 min-h-[200px]">
                      {tasks.filter(t => t.status === 'inprogress').map(task => (
                        <Card key={task.id} className="border border-blue-200 hover:shadow-md transition-shadow">
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between mb-2">
                              <h5 className="font-semibold text-sm text-slate-900 flex-1">{task.title}</h5>
                              <Badge className={`text-xs ${
                                task.priority === 'high' ? 'bg-red-500' :
                                task.priority === 'medium' ? 'bg-yellow-500' :
                                'bg-green-500'
                              }`}>
                                {task.priority}
                              </Badge>
                            </div>
                            {task.description && (
                              <p className="text-xs text-slate-600 mb-3">{task.description}</p>
                            )}
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 text-xs bg-green-50"
                                onClick={() => {
                                  setTasks(tasks.map(t => 
                                    t.id === task.id ? {...t, status: 'done'} : t
                                  ));
                                  toast({
                                    title: "🎉 Task Completed!",
                                    description: task.title
                                  });
                                }}
                              >
                                ✓ Complete
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setTasks(tasks.filter(t => t.id !== task.id))}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      {tasks.filter(t => t.status === 'inprogress').length === 0 && (
                        <p className="text-center text-slate-400 text-sm py-8">No tasks</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Done Column */}
                  <Card className="border-2 border-green-200">
                    <CardHeader className="pb-3 bg-green-50">
                      <CardTitle className="text-sm font-semibold text-green-700">✅ Done</CardTitle>
                      <p className="text-xs text-green-500">{tasks.filter(t => t.status === 'done').length} tasks</p>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-2 min-h-[200px]">
                      {tasks.filter(t => t.status === 'done').map(task => (
                        <Card key={task.id} className="border border-green-200 bg-green-50/50">
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between mb-2">
                              <h5 className="font-semibold text-sm text-slate-700 flex-1 line-through">{task.title}</h5>
                              <Badge className="text-xs bg-green-600">
                                Done
                              </Badge>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="w-full text-xs"
                              onClick={() => setTasks(tasks.filter(t => t.id !== task.id))}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Remove
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                      {tasks.filter(t => t.status === 'done').length === 0 && (
                        <p className="text-center text-slate-400 text-sm py-8">No completed tasks</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* List View */}
              {projectView === 'list' && (
                <div className="space-y-2">
                  {tasks.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Table className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                      <p className="text-lg font-medium">No tasks yet</p>
                      <p className="text-sm">Add your first task above</p>
                    </div>
                  ) : (
                    tasks.map(task => (
                      <Card key={task.id} className="border border-slate-200">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h5 className="font-semibold text-slate-900">{task.title}</h5>
                                <Badge className={`text-xs ${
                                  task.priority === 'high' ? 'bg-red-500' :
                                  task.priority === 'medium' ? 'bg-yellow-500' :
                                  'bg-green-500'
                                }`}>
                                  {task.priority}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {task.status === 'todo' ? '📋 To Do' :
                                   task.status === 'inprogress' ? '🔄 In Progress' :
                                   '✅ Done'}
                                </Badge>
                              </div>
                              {task.description && (
                                <p className="text-sm text-slate-600">{task.description}</p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setTasks(tasks.filter(t => t.id !== task.id))}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              )}

              {/* Sprints View */}
              {projectView === 'sprints' && (
                <div className="space-y-4">
                  <div className="p-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200 text-center">
                    <Clock className="w-12 h-12 mx-auto mb-3 text-purple-600" />
                    <h4 className="font-bold text-lg text-purple-900 mb-2">Sprint Planning Coming Soon!</h4>
                    <p className="text-sm text-purple-700 mb-4">
                      Create time-boxed sprints, set goals, and track velocity
                    </p>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-purple-300">
                      <span className="text-sm font-semibold text-purple-900">Current Tasks:</span>
                      <Badge className="bg-purple-600">{tasks.length}</Badge>
                    </div>
                  </div>
                </div>
              )}

              {/* Design Sprint View - Google Sprint Framework */}
              {projectView === 'design-sprint' && (
                <div className="space-y-4">
                  {!activeDesignSprint ? (
                    <div className="space-y-4">
                      <div className="p-6 bg-gradient-to-r from-blue-50 via-cyan-50 to-teal-50 rounded-lg border-2 border-blue-200">
                        <div className="text-center mb-6">
                          <Zap className="w-16 h-16 mx-auto mb-4 text-blue-600" />
                          <h4 className="font-bold text-2xl text-blue-900 mb-2">Google Design Sprint</h4>
                          <p className="text-sm text-blue-700 max-w-xl mx-auto">
                            A proven 5-day process for solving big challenges, testing ideas, and moving closer to launch
                          </p>
                        </div>
                        
                        <div className="grid md:grid-cols-5 gap-3 mb-6">
                          {[
                            { day: 1, title: 'Understand', icon: '🎯', desc: 'Map the problem' },
                            { day: 2, title: 'Sketch', icon: '✏️', desc: 'Sketch solutions' },
                            { day: 3, title: 'Decide', icon: '🤔', desc: 'Choose the best' },
                            { day: 4, title: 'Prototype', icon: '🛠️', desc: 'Build it' },
                            { day: 5, title: 'Test', icon: '🧪', desc: 'Get feedback' }
                          ].map((day) => (
                            <Card key={day.day} className="border-2 border-blue-200 bg-white">
                              <CardContent className="p-3 text-center">
                                <div className="text-3xl mb-2">{day.icon}</div>
                                <h5 className="font-bold text-sm text-blue-900 mb-1">Day {day.day}</h5>
                                <p className="text-xs font-semibold text-blue-700">{day.title}</p>
                                <p className="text-xs text-slate-600">{day.desc}</p>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                        
                        <Button
                          onClick={() => {
                            const newSprint = {
                              id: Date.now().toString(),
                              title: sessionTitle || 'Design Sprint',
                              started_at: new Date().toISOString(),
                              current_day: 1,
                              days: {
                                1: { completed: false, activities: [], notes: '', files: [] },
                                2: { completed: false, activities: [], notes: '', files: [] },
                                3: { completed: false, activities: [], notes: '', files: [] },
                                4: { completed: false, activities: [], notes: '', files: [] },
                                5: { completed: false, activities: [], notes: '', files: [] }
                              }
                            };
                            setDesignSprints([...designSprints, newSprint]);
                            setActiveDesignSprint(newSprint);
                            setCurrentSprintDay(1);
                            toast({
                              title: "🚀 Design Sprint Started!",
                              description: "Day 1: Understand - Let's map the problem"
                            });
                          }}
                          size="lg"
                          className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white"
                        >
                          <Zap className="w-5 h-5 mr-2" />
                          Start 5-Day Design Sprint
                        </Button>
                      </div>
                      
                      {designSprints.length > 0 && (
                        <Card className="border-2 border-slate-200">
                          <CardHeader>
                            <CardTitle className="text-lg">Previous Sprints</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            {designSprints.map(sprint => (
                              <Card key={sprint.id} className="border border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                                    onClick={() => {
                                      setActiveDesignSprint(sprint);
                                      setCurrentSprintDay(sprint.current_day);
                                    }}>
                                <CardContent className="p-4">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h5 className="font-semibold text-slate-900">{sprint.title}</h5>
                                      <p className="text-xs text-slate-600">
                                        Started: {new Date(sprint.started_at).toLocaleDateString()}
                                      </p>
                                    </div>
                                    <Badge className="bg-blue-600">
                                      Day {sprint.current_day}/5
                                    </Badge>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Sprint Header */}
                      <Card className="border-2 border-blue-400 bg-gradient-to-r from-blue-50 to-cyan-50">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setActiveDesignSprint(null)}
                              >
                                ← Back
                              </Button>
                              <div>
                                <h4 className="font-bold text-lg text-blue-900">{activeDesignSprint.title}</h4>
                                <p className="text-xs text-blue-600">Started: {new Date(activeDesignSprint.started_at).toLocaleDateString()}</p>
                              </div>
                            </div>
                            <Badge className="text-lg px-4 py-2 bg-blue-600">
                              Day {currentSprintDay}/5
                            </Badge>
                          </div>
                          
                          {/* Day Progress */}
                          <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map(day => (
                              <div
                                key={day}
                                className={`flex-1 h-2 rounded-full transition-all ${
                                  day < currentSprintDay ? 'bg-green-500' :
                                  day === currentSprintDay ? 'bg-blue-500' :
                                  'bg-slate-200'
                                }`}
                              />
                            ))}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Day Navigation */}
                      <div className="grid grid-cols-5 gap-2">
                        {[
                          { day: 1, title: 'Understand', icon: '🎯' },
                          { day: 2, title: 'Sketch', icon: '✏️' },
                          { day: 3, title: 'Decide', icon: '🤔' },
                          { day: 4, title: 'Prototype', icon: '🛠️' },
                          { day: 5, title: 'Test', icon: '🧪' }
                        ].map((day) => (
                          <Button
                            key={day.day}
                            variant={currentSprintDay === day.day ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCurrentSprintDay(day.day)}
                            className={`flex-col h-auto py-2 ${
                              activeDesignSprint.days[day.day].completed ? 'border-green-500 bg-green-50' : ''
                            }`}
                          >
                            <span className="text-xl mb-1">{day.icon}</span>
                            <span className="text-xs font-semibold">{day.title}</span>
                            {activeDesignSprint.days[day.day].completed && (
                              <span className="text-xs text-green-600">✓</span>
                            )}
                          </Button>
                        ))}
                      </div>

                      {/* Day Content */}
                      <Card className="border-2 border-blue-200">
                        <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
                          <CardTitle className="text-xl">
                            {currentSprintDay === 1 && '🎯 Day 1: Understand & Map'}
                            {currentSprintDay === 2 && '✏️ Day 2: Sketch Solutions'}
                            {currentSprintDay === 3 && '🤔 Day 3: Decide & Storyboard'}
                            {currentSprintDay === 4 && '🛠️ Day 4: Prototype'}
                            {currentSprintDay === 5 && '🧪 Day 5: Test with Users'}
                          </CardTitle>
                          <p className="text-sm text-slate-600 mt-1">
                            {currentSprintDay === 1 && 'Define the problem, set long-term goal, map the customer journey'}
                            {currentSprintDay === 2 && 'Each person sketches solutions, focusing on critical aspects'}
                            {currentSprintDay === 3 && 'Critique solutions, make decisions, create storyboard'}
                            {currentSprintDay === 4 && 'Build a realistic facade of your product or service'}
                            {currentSprintDay === 5 && 'Interview customers and learn by watching them interact'}
                          </p>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-4">
                          {/* Activities Checklist */}
                          <div>
                            <h5 className="font-semibold text-sm text-slate-900 mb-3">Key Activities:</h5>
                            <div className="space-y-2">
                              {currentSprintDay === 1 && [
                                'Set long-term goal',
                                'Map out the challenge',
                                'Ask the experts',
                                'Target: Choose target customer & event'
                              ].map((activity, idx) => (
                                <label key={idx} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                                  <input
                                    type="checkbox"
                                    checked={activeDesignSprint.days[currentSprintDay].activities.includes(activity)}
                                    onChange={(e) => {
                                      const updatedSprint = {...activeDesignSprint};
                                      if (e.target.checked) {
                                        updatedSprint.days[currentSprintDay].activities.push(activity);
                                      } else {
                                        updatedSprint.days[currentSprintDay].activities = 
                                          updatedSprint.days[currentSprintDay].activities.filter(a => a !== activity);
                                      }
                                      setActiveDesignSprint(updatedSprint);
                                      setDesignSprints(designSprints.map(s => s.id === updatedSprint.id ? updatedSprint : s));
                                    }}
                                    className="w-4 h-4"
                                  />
                                  <span className="text-sm text-slate-700">{activity}</span>
                                </label>
                              ))}
                              
                              {currentSprintDay === 2 && [
                                'Lightning demos',
                                'Divide or swarm',
                                'Sketch individually (4-step sketch)',
                                'Crazy 8s exercise'
                              ].map((activity, idx) => (
                                <label key={idx} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                                  <input
                                    type="checkbox"
                                    checked={activeDesignSprint.days[currentSprintDay].activities.includes(activity)}
                                    onChange={(e) => {
                                      const updatedSprint = {...activeDesignSprint};
                                      if (e.target.checked) {
                                        updatedSprint.days[currentSprintDay].activities.push(activity);
                                      } else {
                                        updatedSprint.days[currentSprintDay].activities = 
                                          updatedSprint.days[currentSprintDay].activities.filter(a => a !== activity);
                                      }
                                      setActiveDesignSprint(updatedSprint);
                                      setDesignSprints(designSprints.map(s => s.id === updatedSprint.id ? updatedSprint : s));
                                    }}
                                    className="w-4 h-4"
                                  />
                                  <span className="text-sm text-slate-700">{activity}</span>
                                </label>
                              ))}
                              
                              {currentSprintDay === 3 && [
                                'Art museum (review sketches)',
                                'Heat map voting',
                                'Speed critique',
                                'Storyboard',
                                'Decide on solution'
                              ].map((activity, idx) => (
                                <label key={idx} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                                  <input
                                    type="checkbox"
                                    checked={activeDesignSprint.days[currentSprintDay].activities.includes(activity)}
                                    onChange={(e) => {
                                      const updatedSprint = {...activeDesignSprint};
                                      if (e.target.checked) {
                                        updatedSprint.days[currentSprintDay].activities.push(activity);
                                      } else {
                                        updatedSprint.days[currentSprintDay].activities = 
                                          updatedSprint.days[currentSprintDay].activities.filter(a => a !== activity);
                                      }
                                      setActiveDesignSprint(updatedSprint);
                                      setDesignSprints(designSprints.map(s => s.id === updatedSprint.id ? updatedSprint : s));
                                    }}
                                    className="w-4 h-4"
                                  />
                                  <span className="text-sm text-slate-700">{activity}</span>
                                </label>
                              ))}
                              
                              {currentSprintDay === 4 && [
                                'Pick the right tools',
                                'Divide and conquer',
                                'Stitch it together',
                                'Do a trial run'
                              ].map((activity, idx) => (
                                <label key={idx} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                                  <input
                                    type="checkbox"
                                    checked={activeDesignSprint.days[currentSprintDay].activities.includes(activity)}
                                    onChange={(e) => {
                                      const updatedSprint = {...activeDesignSprint};
                                      if (e.target.checked) {
                                        updatedSprint.days[currentSprintDay].activities.push(activity);
                                      } else {
                                        updatedSprint.days[currentSprintDay].activities = 
                                          updatedSprint.days[currentSprintDay].activities.filter(a => a !== activity);
                                      }
                                      setActiveDesignSprint(updatedSprint);
                                      setDesignSprints(designSprints.map(s => s.id === updatedSprint.id ? updatedSprint : s));
                                    }}
                                    className="w-4 h-4"
                                  />
                                  <span className="text-sm text-slate-700">{activity}</span>
                                </label>
                              ))}
                              
                              {currentSprintDay === 5 && [
                                'Five is the magic number',
                                'Write interview script',
                                'Conduct interviews',
                                'Take detailed notes',
                                'Identify patterns'
                              ].map((activity, idx) => (
                                <label key={idx} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                                  <input
                                    type="checkbox"
                                    checked={activeDesignSprint.days[currentSprintDay].activities.includes(activity)}
                                    onChange={(e) => {
                                      const updatedSprint = {...activeDesignSprint};
                                      if (e.target.checked) {
                                        updatedSprint.days[currentSprintDay].activities.push(activity);
                                      } else {
                                        updatedSprint.days[currentSprintDay].activities = 
                                          updatedSprint.days[currentSprintDay].activities.filter(a => a !== activity);
                                      }
                                      setActiveDesignSprint(updatedSprint);
                                      setDesignSprints(designSprints.map(s => s.id === updatedSprint.id ? updatedSprint : s));
                                    }}
                                    className="w-4 h-4"
                                  />
                                  <span className="text-sm text-slate-700">{activity}</span>
                                </label>
                              ))}
                            </div>
                          </div>

                          {/* Notes Section */}
                          <div>
                            <h5 className="font-semibold text-sm text-slate-900 mb-2">Day Notes:</h5>
                            <Textarea
                              value={activeDesignSprint.days[currentSprintDay].notes}
                              onChange={(e) => {
                                const updatedSprint = {...activeDesignSprint};
                                updatedSprint.days[currentSprintDay].notes = e.target.value;
                                setActiveDesignSprint(updatedSprint);
                                setDesignSprints(designSprints.map(s => s.id === updatedSprint.id ? updatedSprint : s));
                              }}
                              placeholder="Capture your insights, decisions, and key takeaways from today..."
                              rows={4}
                              className="resize-none"
                            />
                          </div>

                          {/* Document Attachments */}
                          <div>
                            <h5 className="font-semibold text-sm text-slate-900 mb-2">📎 Attachments:</h5>
                            <div className="space-y-2">
                              <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-all">
                                <input
                                  type="file"
                                  className="hidden"
                                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.sketch,.fig"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      const updatedSprint = {...activeDesignSprint};
                                      if (!updatedSprint.days[currentSprintDay].files) {
                                        updatedSprint.days[currentSprintDay].files = [];
                                      }
                                      updatedSprint.days[currentSprintDay].files.push({
                                        name: file.name,
                                        size: file.size,
                                        type: file.type,
                                        uploaded_at: new Date().toISOString()
                                      });
                                      setActiveDesignSprint(updatedSprint);
                                      setDesignSprints(designSprints.map(s => s.id === updatedSprint.id ? updatedSprint : s));
                                      toast({
                                        title: "📎 File Attached!",
                                        description: file.name
                                      });
                                    }
                                  }}
                                />
                                <Plus className="w-4 h-4 text-slate-600" />
                                <span className="text-sm text-slate-600">Upload sketch, prototype, or document</span>
                              </label>
                              
                              {activeDesignSprint.days[currentSprintDay].files && 
                               activeDesignSprint.days[currentSprintDay].files.length > 0 && (
                                <div className="space-y-2">
                                  {activeDesignSprint.days[currentSprintDay].files.map((file, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-slate-200">
                                      <div className="flex items-center gap-2 flex-1">
                                        <span className="text-xl">
                                          {file.name.endsWith('.pdf') ? '📄' :
                                           file.name.match(/\.(jpg|jpeg|png)/) ? '🖼️' :
                                           file.name.endsWith('.sketch') ? '✏️' :
                                           file.name.endsWith('.fig') ? '🎨' : '📎'}
                                        </span>
                                        <div className="flex-1">
                                          <p className="text-sm font-medium text-slate-900">{file.name}</p>
                                          <p className="text-xs text-slate-500">
                                            {(file.size / 1024).toFixed(1)} KB • {new Date(file.uploaded_at).toLocaleTimeString()}
                                          </p>
                                        </div>
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                          const updatedSprint = {...activeDesignSprint};
                                          updatedSprint.days[currentSprintDay].files = 
                                            updatedSprint.days[currentSprintDay].files.filter((_, i) => i !== idx);
                                          setActiveDesignSprint(updatedSprint);
                                          setDesignSprints(designSprints.map(s => s.id === updatedSprint.id ? updatedSprint : s));
                                        }}
                                      >
                                        <Trash2 className="w-4 h-4 text-slate-400" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-2">
                              💡 Upload sketches, wireframes, prototypes, research docs, or test results
                            </p>
                          </div>

                          {/* Day Actions */}
                          <div className="flex gap-2 pt-4 border-t">
                            {currentSprintDay > 1 && (
                              <Button
                                variant="outline"
                                onClick={() => setCurrentSprintDay(currentSprintDay - 1)}
                              >
                                ← Previous Day
                              </Button>
                            )}
                            
                            <Button
                              className={`flex-1 ${
                                activeDesignSprint.days[currentSprintDay].completed 
                                  ? 'bg-green-600' 
                                  : 'bg-blue-600'
                              }`}
                              onClick={() => {
                                const updatedSprint = {...activeDesignSprint};
                                updatedSprint.days[currentSprintDay].completed = !updatedSprint.days[currentSprintDay].completed;
                                setActiveDesignSprint(updatedSprint);
                                setDesignSprints(designSprints.map(s => s.id === updatedSprint.id ? updatedSprint : s));
                                
                                if (!activeDesignSprint.days[currentSprintDay].completed) {
                                  toast({
                                    title: `✅ Day ${currentSprintDay} Completed!`,
                                    description: currentSprintDay < 5 ? 'Move to next day when ready' : 'Sprint complete! 🎉'
                                  });
                                }
                              }}
                            >
                              {activeDesignSprint.days[currentSprintDay].completed ? '✓ Completed' : 'Mark Day Complete'}
                            </Button>
                            
                            {currentSprintDay < 5 && (
                              <Button
                                variant="outline"
                                onClick={() => setCurrentSprintDay(currentSprintDay + 1)}
                              >
                                Next Day →
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Notice Board Tab */}
          {activeTab === 'noticeboard' && (
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Notice Board: Pin Your Key Resources
                </h3>
                <p className="text-sm text-blue-800">Save important links, papers, and references for easy access throughout your research journey</p>
              </div>

              {/* Add New Pin */}
              <Card className="border-2 border-dashed border-blue-300 bg-blue-50/50">
                <CardContent className="pt-4">
                  <h4 className="font-semibold text-sm text-blue-900 mb-3">📌 Pin New Resource</h4>
                  <div className="space-y-3">
                    <Input
                      placeholder="Resource title (e.g., Key paper on algae CO2 capture)"
                      value={newPinTitle}
                      onChange={(e) => setNewPinTitle(e.target.value)}
                      className="bg-white"
                    />
                    <Input
                      placeholder="URL or reference"
                      value={newPinUrl}
                      onChange={(e) => setNewPinUrl(e.target.value)}
                      className="bg-white"
                    />
                    <Textarea
                      placeholder="Quick notes (why is this important?)"
                      value={newPinNotes}
                      onChange={(e) => setNewPinNotes(e.target.value)}
                      rows={2}
                      className="bg-white resize-none"
                    />
                    <Button
                      onClick={async () => {
                        if (!newPinTitle.trim()) return;
                        
                        setLoading(true);
                        
                        // Get AI summary if URL provided
                        let aiSummary = '';
                        if (newPinUrl.trim()) {
                          try {
                            const summaryRes = await axios.post(`${BACKEND_URL}/api/thinklab/summarize-url`, {
                              url: newPinUrl,
                              title: newPinTitle
                            });
                            if (summaryRes.data.success) {
                              aiSummary = summaryRes.data.summary;
                            }
                          } catch (error) {
                            console.error('Summary generation failed:', error);
                          }
                        }
                        
                        const newPin = {
                          id: Date.now().toString(),
                          title: newPinTitle,
                          url: newPinUrl,
                          notes: newPinNotes,
                          ai_summary: aiSummary,
                          pinned_at: new Date().toISOString()
                        };
                        
                        setPinnedResources([newPin, ...pinnedResources]);
                        setNewPinTitle('');
                        setNewPinUrl('');
                        setNewPinNotes('');
                        setLoading(false);
                        
                        toast({
                          title: "📌 Resource Pinned!",
                          description: aiSummary ? "With AI summary" : `"${newPin.title}" added to notice board`
                        });
                      }}
                      className="w-full bg-blue-600"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating summary...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Pin Resource
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Pinned Resources Grid */}
              {pinnedResources.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {pinnedResources.map((pin) => (
                    <Card key={pin.id} className="border-2 border-blue-200 bg-gradient-to-br from-white to-blue-50 hover:shadow-lg transition-all">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-2xl">📌</span>
                              <h5 className="font-semibold text-slate-900">{pin.title}</h5>
                            </div>
                            {pin.url && (
                              <a
                                href={pin.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 underline break-all block mb-2"
                              >
                                {pin.url.length > 50 ? pin.url.substring(0, 50) + '...' : pin.url} →
                              </a>
                            )}
                            {pin.ai_summary && (
                              <div className="text-sm text-slate-700 bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-lg border border-purple-200 mb-2">
                                <div className="flex items-start gap-2">
                                  <Zap className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                                  <p className="italic">{pin.ai_summary}</p>
                                </div>
                              </div>
                            )}
                            {pin.notes && (
                              <p className="text-sm text-slate-600 italic bg-yellow-50 p-2 rounded border border-yellow-200 mb-2">
                                "{pin.notes}"
                              </p>
                            )}
                            <p className="text-xs text-slate-500">
                              Pinned: {new Date(pin.pinned_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setPinnedResources(pinnedResources.filter(p => p.id !== pin.id))}
                            className="ml-2"
                          >
                            <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600" />
                          </Button>
                        </div>
                        
                        {/* Quick Actions */}
                        <div className="flex gap-2 mt-3 pt-3 border-t">
                          {pin.url && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 text-xs"
                              onClick={() => window.open(pin.url, '_blank')}
                            >
                              Open Link
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-xs"
                            onClick={() => {
                              // Copy to clipboard
                              navigator.clipboard.writeText(pin.url || pin.title);
                              toast({
                                title: "Copied!",
                                description: "Link copied to clipboard"
                              });
                            }}
                          >
                            Copy
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                  <p className="text-lg font-medium mb-2">No pinned resources yet</p>
                  <p className="text-sm">Pin important links and references for easy access during long research sessions</p>
                </div>
              )}
              
              {pinnedResources.length > 0 && (
                <Card className="border-2 border-green-200 bg-green-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-green-900">📊 Your Reference Library</h4>
                        <p className="text-sm text-green-700">{pinnedResources.length} resources pinned</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const exportData = JSON.stringify(pinnedResources, null, 2);
                          const blob = new Blob([exportData], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `notice-board-${sessionTitle || 'resources'}.json`;
                          a.click();
                          toast({
                            title: "📥 Exported!",
                            description: "Resources saved to JSON file"
                          });
                        }}
                      >
                        Export
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          {/* Journal Tab */}
          {activeTab === 'journal' && (
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
                <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Research Journal: Capture Feedback & Insights
                </h3>
                <p className="text-sm text-amber-800">Document conversations with professors, customer feedback, stakeholder interviews, and personal reflections</p>
              </div>

              {/* Add New Entry */}
              <Card className="border-2 border-dashed border-amber-300 bg-amber-50/50">
                <CardContent className="pt-4">
                  <h4 className="font-semibold text-sm text-amber-900 mb-3">✍️ New Journal Entry</h4>
                  <div className="space-y-3">
                    <select
                      value={journalSource}
                      onChange={(e) => setJournalSource(e.target.value)}
                      className="w-full p-2 border rounded-lg text-sm"
                    >
                      <option value="general">📝 General Thought</option>
                      <option value="professor">👨‍🏫 Professor Feedback</option>
                      <option value="customer">👥 Customer Interview</option>
                      <option value="stakeholder">🤝 Stakeholder Meeting</option>
                      <option value="personal">💭 Personal Reflection</option>
                    </select>
                    
                    <Textarea
                      placeholder="What did you learn? What feedback did you receive? What insights emerged?"
                      value={newJournalEntry}
                      onChange={(e) => setNewJournalEntry(e.target.value)}
                      rows={6}
                      className="bg-white resize-none"
                    />
                    
                    <Button
                      onClick={() => {
                        if (!newJournalEntry.trim()) return;
                        
                        const entry = {
                          id: Date.now().toString(),
                          content: newJournalEntry,
                          source: journalSource,
                          created_at: new Date().toISOString()
                        };
                        
                        setJournalEntries([entry, ...journalEntries]);
                        setNewJournalEntry('');
                        
                        toast({
                          title: "✍️ Entry Added!",
                          description: `${journalSource} entry saved to journal`
                        });
                      }}
                      className="w-full bg-amber-600"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Entry
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Journal Entries Timeline */}
              {journalEntries.length > 0 ? (
                <div className="space-y-3">
                  {journalEntries.map((entry) => (
                    <Card key={entry.id} className="border-2 border-amber-200 bg-gradient-to-br from-white to-amber-50/50">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">
                              {entry.source === 'professor' ? '👨‍🏫' :
                               entry.source === 'customer' ? '👥' :
                               entry.source === 'stakeholder' ? '🤝' :
                               entry.source === 'personal' ? '💭' : '📝'}
                            </span>
                            <div>
                              <Badge variant="outline" className="text-xs">
                                {entry.source === 'professor' ? 'Professor' :
                                 entry.source === 'customer' ? 'Customer' :
                                 entry.source === 'stakeholder' ? 'Stakeholder' :
                                 entry.source === 'personal' ? 'Personal' : 'General'}
                              </Badge>
                              <p className="text-xs text-slate-500 mt-1">
                                {new Date(entry.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setJournalEntries(journalEntries.filter(e => e.id !== entry.id))}
                          >
                            <Trash2 className="w-4 h-4 text-slate-400" />
                          </Button>
                        </div>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                          {entry.content}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                  <p className="text-lg font-medium mb-2">No journal entries yet</p>
                  <p className="text-sm">Start capturing feedback, insights, and reflections</p>
                </div>
              )}
              
              {journalEntries.length > 0 && (
                <Card className="border-2 border-green-200 bg-green-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-green-900">📊 Your Research Journal</h4>
                        <p className="text-sm text-green-700">{journalEntries.length} entries documented</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const exportData = journalEntries.map(e => ({
                            date: new Date(e.created_at).toLocaleString(),
                            source: e.source,
                            content: e.content
                          }));
                          const text = exportData.map(e => 
                            `[${e.date}] ${e.source.toUpperCase()}\n${e.content}\n\n`
                          ).join('---\n\n');
                          const blob = new Blob([text], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `journal-${sessionTitle || 'entries'}.txt`;
                          a.click();
                          toast({
                            title: "📥 Exported!",
                            description: "Journal saved to text file"
                          });
                        }}
                      >
                        Export
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          {/* Stakeholder Analysis Tab */}
          {activeTab === 'stakeholders' && (
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg border border-violet-200">
                <h3 className="font-semibold text-violet-900 mb-2 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Who Are We Solving For?
                </h3>
                <p className="text-sm text-violet-800">AI-powered stakeholder mapping and impact analysis using design thinking frameworks</p>
              </div>

              {!stakeholderData ? (
                <Card className="border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-white">
                  <CardContent className="pt-6 pb-6 text-center">
                    <Users className="w-16 h-16 mx-auto mb-4 text-violet-600" />
                    <h4 className="text-lg font-bold text-violet-900 mb-2">Generate Stakeholder Map</h4>
                    <p className="text-sm text-violet-700 mb-6 max-w-md mx-auto">
                      AI will analyze your project and identify primary users, secondary stakeholders, decision makers, and potential blockers
                    </p>
                    <Button
                      onClick={async () => {
                        setLoadingStakeholders(true);
                        try {
                          const response = await axios.post(`${BACKEND_URL}/api/thinklab/analyze-stakeholders`, {
                            session_id: sessionId,
                            project_description: sessionTitle
                          });
                          
                          if (response.data.success) {
                            setStakeholderData(response.data.data);
                            toast({
                              title: "🎯 Analysis Complete!",
                              description: "Stakeholder map generated"
                            });
                          }
                        } catch (error) {
                          toast({
                            title: "Analysis Failed",
                            description: "Could not generate stakeholder map",
                            variant: "destructive"
                          });
                        }
                        setLoadingStakeholders(false);
                      }}
                      size="lg"
                      className="bg-gradient-to-r from-violet-600 to-purple-600"
                      disabled={loadingStakeholders}
                    >
                      {loadingStakeholders ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Zap className="w-5 h-5 mr-2" />
                          Generate Stakeholder Map
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {/* Summary */}
                  <Card className="border-2 border-violet-300 bg-gradient-to-r from-violet-50 to-purple-50">
                    <CardContent className="p-4">
                      <h4 className="font-semibold text-violet-900 mb-2">📊 Overview</h4>
                      <p className="text-sm text-violet-800">{stakeholderData.summary}</p>
                    </CardContent>
                  </Card>

                  {/* Primary Users */}
                  <Card className="border-2 border-blue-200">
                    <CardHeader className="pb-3 bg-blue-50">
                      <CardTitle className="text-sm font-bold text-blue-900">👥 Primary Users</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 pt-4">
                      {stakeholderData.primary_users?.map((user, idx) => (
                        <div key={idx} className="p-3 bg-white rounded-lg border border-blue-200">
                          <div className="flex items-start justify-between mb-2">
                            <h5 className="font-semibold text-slate-900">{user.group}</h5>
                            <Badge className={`text-xs ${
                              user.influence === 'high' ? 'bg-red-500' :
                              user.influence === 'medium' ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}>
                              {user.influence} influence
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-700 mb-2"><strong>Need:</strong> {user.need}</p>
                          <p className="text-sm text-slate-600"><strong>Impact:</strong> {user.impact}</p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Secondary Stakeholders */}
                  <Card className="border-2 border-green-200">
                    <CardHeader className="pb-3 bg-green-50">
                      <CardTitle className="text-sm font-bold text-green-900">🤝 Secondary Stakeholders</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 pt-4">
                      {stakeholderData.secondary_stakeholders?.map((stakeholder, idx) => (
                        <div key={idx} className="p-3 bg-white rounded-lg border border-green-200">
                          <div className="flex items-start justify-between mb-2">
                            <h5 className="font-semibold text-slate-900">{stakeholder.group}</h5>
                            <Badge className={`text-xs ${
                              stakeholder.influence === 'high' ? 'bg-red-500' :
                              stakeholder.influence === 'medium' ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}>
                              {stakeholder.influence} influence
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-700 mb-2"><strong>Need:</strong> {stakeholder.need}</p>
                          <p className="text-sm text-slate-600"><strong>Impact:</strong> {stakeholder.impact}</p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Decision Makers */}
                  <Card className="border-2 border-purple-200">
                    <CardHeader className="pb-3 bg-purple-50">
                      <CardTitle className="text-sm font-bold text-purple-900">⭐ Key Decision Makers</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 pt-4">
                      {stakeholderData.decision_makers?.map((dm, idx) => (
                        <div key={idx} className="p-3 bg-white rounded-lg border border-purple-200">
                          <div className="flex items-start justify-between mb-2">
                            <h5 className="font-semibold text-slate-900">{dm.group}</h5>
                            <Badge className="text-xs bg-purple-600">{dm.influence} influence</Badge>
                          </div>
                          <p className="text-sm text-slate-700 mb-2"><strong>Need:</strong> {dm.need}</p>
                          <p className="text-sm text-slate-600"><strong>Impact:</strong> {dm.impact}</p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Potential Blockers */}
                  {stakeholderData.potential_blockers && stakeholderData.potential_blockers.length > 0 && (
                    <Card className="border-2 border-red-200">
                      <CardHeader className="pb-3 bg-red-50">
                        <CardTitle className="text-sm font-bold text-red-900">⚠️ Potential Blockers</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 pt-4">
                        {stakeholderData.potential_blockers.map((blocker, idx) => (
                          <div key={idx} className="p-3 bg-white rounded-lg border border-red-200">
                            <div className="flex items-start justify-between mb-2">
                              <h5 className="font-semibold text-slate-900">{blocker.group}</h5>
                              <Badge className="text-xs bg-red-600">{blocker.influence} influence</Badge>
                            </div>
                            <p className="text-sm text-slate-700 mb-2"><strong>Concern:</strong> {blocker.need}</p>
                            <p className="text-sm text-slate-600"><strong>Impact:</strong> {blocker.impact}</p>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Key Insights */}
                  <Card className="border-2 border-amber-200 bg-amber-50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-bold text-amber-900">💡 Key Insights</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 pt-4">
                      {stakeholderData.key_insights?.map((insight, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <span className="text-amber-600 font-bold">{idx + 1}.</span>
                          <p className="text-sm text-slate-700">{insight}</p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setStakeholderData(null)}
                  >
                    Generate New Analysis
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Smart Alarm Modal */}
      {showAlarm && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-white shadow-2xl animate-in zoom-in">
            <CardContent className="pt-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Clock className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  {alarmType === 'initial' ? "⏰ Time's Up!" : "🔔 Snooze Time's Up!"}
                </h3>
                <p className="text-slate-600">
                  {alarmType === 'initial' 
                    ? "You've been reading for 30 minutes"
                    : 'Your 10-minute snooze is over'}
                </p>
              </div>

              {!showSparkInput ? (
                <div className="space-y-3">
                  {alarmType === 'snooze' && (
                    <Button
                      onClick={() => setShowSparkInput(true)}
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
                      size="lg"
                    >
                      💡 Found a Spark?
                    </Button>
                  )}
                  
                  {alarmType === 'initial' && (
                    <>
                      <Button
                        onClick={handleAlarmSnooze}
                        className="w-full bg-orange-600"
                        size="lg"
                      >
                        ⏰ Snooze 10 Minutes
                      </Button>
                      <Button
                        onClick={() => {
                          setShowAlarm(false);
                          setActiveTab('ideaboard');
                        }}
                        variant="outline"
                        className="w-full"
                        size="lg"
                      >
                        📝 Go to Idea Board
                      </Button>
                    </>
                  )}
                  
                  <Button
                    onClick={handleAlarmDismiss}
                    variant="ghost"
                    className="w-full"
                  >
                    🔕 Quiet/Dismiss - Return Later
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center">
                    <p className="text-sm font-semibold text-purple-900 mb-2">
                      Quick capture (max 5 words):
                    </p>
                    <Input
                      value={sparkText}
                      onChange={(e) => {
                        const words = e.target.value.split(' ').filter(w => w.length > 0);
                        if (words.length <= 5) {
                          setSparkText(e.target.value);
                        }
                      }}
                      placeholder="e.g., AI-powered sustainable farming"
                      className="text-center"
                      autoFocus
                    />
                    <p className="text-xs text-purple-700 mt-1">
                      {5 - sparkText.split(' ').filter(w => w.length > 0).length} words left
                    </p>
                  </div>
                  <Button
                    onClick={handleQuickSpark}
                    disabled={!sparkText.trim() || sparkText.split(' ').filter(w => w.length > 0).length > 5}
                    className="w-full bg-purple-600"
                    size="lg"
                  >
                    ✨ Save Spark
                  </Button>
                  <Button
                    onClick={() => {
                      setShowSparkInput(false);
                      setShowAlarm(false);
                      setActiveTab('ideaboard');
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    📝 Write Longer Note
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Resource Dialog */}
      {showResourceDialog && pendingResource && (
        <div className="fixed bottom-24 right-8 z-50 animate-in slide-in-from-bottom-5">
          <Card className="w-96 border-2 border-green-400 shadow-2xl bg-white">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  pendingResource.type === 'paper' ? 'bg-blue-100' : 'bg-red-100'
                }`}>
                  {pendingResource.type === 'paper' ? '📄' : '🎥'}
                </div>
                <div className="flex-1">
                  <p className="text-xs text-green-600 font-semibold mb-1">NEW RESOURCE FOUND</p>
                  <h4 className="font-semibold text-slate-900 text-sm">{pendingResource.title}</h4>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleKeepResource}
                  className="flex-1 bg-green-600"
                  size="sm"
                >
                  Keep
                </Button>
                <Button
                  onClick={handleDismissResource}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  Dismiss
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ThinkLab;
